/**
 * ProblemTemplates.js — Task #219
 *
 * Fixed, trusted renderers for the Brilliant-style interaction templates. The
 * generative engine returns pure DATA (a `spec`); these renderers turn that
 * data into an interactive widget. There is NO code in the payload and nothing
 * here ever evals generated content — that is what "sandboxed generated
 * content (cannot touch the app)" means in practice.
 *
 * Every renderer returns a uniform instance:
 *   {
 *     template,            // template id
 *     isComplete(): bool,  // has the learner supplied an answer to check?
 *     check(): {correct},  // evaluate the current state
 *     reset(): void,       // clear the current attempt
 *     highlightTarget(),   // positional/gaze hint — pulse the interactive zone
 *     destroy(): void
 *   }
 *
 * Callbacks (all optional):
 *   onInteract()  — fired on any learner action (resets the stall timer)
 *   onComplete()  — fired the moment the widget becomes answerable
 */

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pulse(node) {
  if (!node) return;
  node.classList.remove("pl-gaze-pulse");
  // force reflow so the animation re-triggers
  void node.offsetWidth;
  node.classList.add("pl-gaze-pulse");
  setTimeout(() => node && node.classList.remove("pl-gaze-pulse"), 1600);
}

/* ============================ tap_the_right_one ========================== */
function renderTapOne(spec, root, cb) {
  const multi = !!spec.multi;
  const correct = new Set((spec.correct || []).map(String));
  const selected = new Set();
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const grid = el("div", "pl-options");
  (spec.options || []).forEach((o) => {
    const b = el("button", "pl-option");
    b.type = "button";
    b.textContent = o.label;
    b.dataset.id = String(o.id);
    b.addEventListener("click", () => {
      const id = String(o.id);
      if (multi) {
        if (selected.has(id)) { selected.delete(id); b.classList.remove("sel"); }
        else { selected.add(id); b.classList.add("sel"); }
      } else {
        selected.clear();
        grid.querySelectorAll(".pl-option").forEach((x) => x.classList.remove("sel"));
        selected.add(id); b.classList.add("sel");
      }
      cb.onInteract && cb.onInteract();
      if (selected.size && cb.onComplete) cb.onComplete();
    });
    grid.appendChild(b);
  });
  root.appendChild(grid);
  return {
    template: "tap_the_right_one",
    isComplete: () => selected.size > 0,
    check: () => {
      const ok = selected.size === correct.size &&
        [...selected].every((id) => correct.has(id));
      grid.querySelectorAll(".pl-option").forEach((x) => {
        x.classList.toggle("right", ok && x.classList.contains("sel"));
        x.classList.toggle("wrong", !ok && x.classList.contains("sel"));
      });
      return { correct: ok };
    },
    reset: () => {
      selected.clear();
      grid.querySelectorAll(".pl-option").forEach((x) =>
        x.classList.remove("sel", "right", "wrong"));
    },
    highlightTarget: () => pulse(grid),
    destroy: () => { root.innerHTML = ""; },
  };
}

/* ============================ slider_sim ============================== */
// Task #302 — optional authored value→readout mapping. Pure DATA interpreted
// by this fixed template (never evaluated): while the learner drags, a live
// "consequence" line shows what their value MEANS (dose→effect, angle→height),
// so the answer is discovered, not pin-hunted. Shape (validated server-side in
// problem_engine._valid_consequence; malformed blocks are dropped there):
//   spec.consequence = { template: "…{y}…{v}…", decimals?: 0-4,
//     map?: {kind:"linear", m, b} | {kind:"table", stops:[[value, mapped],…]} }
function consequenceValue(map, v) {
  if (map && map.kind === "linear") {
    const m = Number(map.m), b = Number(map.b);
    if (isFinite(m) && isFinite(b)) return m * v + b;
  } else if (map && map.kind === "table" && Array.isArray(map.stops)) {
    const stops = map.stops
      .map((s) => [Number(s && s[0]), Number(s && s[1])])
      .filter((s) => isFinite(s[0]) && isFinite(s[1]))
      .sort((a, b2) => a[0] - b2[0]);
    if (stops.length) {
      if (v <= stops[0][0]) return stops[0][1];
      const last = stops[stops.length - 1];
      if (v >= last[0]) return last[1];
      for (let i = 1; i < stops.length; i++) {
        if (v <= stops[i][0]) {
          const x0 = stops[i - 1][0], y0 = stops[i - 1][1];
          const x1 = stops[i][0], y1 = stops[i][1];
          return x1 === x0 ? y0 : y0 + ((y1 - y0) * (v - x0)) / (x1 - x0);
        }
      }
    }
  }
  return v; // no/unknown map: the consequence IS the value
}

function renderSlider(spec, root, cb) {
  const mn = Number(spec.min), mx = Number(spec.max);
  const step = Number(spec.step) || (mx - mn) / 100;
  const target = Number(spec.target);
  const tol = Number(spec.tolerance) || 0;
  // Task #302 — winnable by construction: grade the CONCEPT region, never a
  // pixel-perfect pin-hunt. At least 4% of the range and at least one step.
  // MUST stay in lockstep with problem_engine._normalize_spec (server side),
  // so problems cached before the rule still grade identically.
  const effTol = Math.max(tol, (mx - mn) * 0.04, step);
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const wrap = el("div", "pl-slider-wrap");
  const readout = el("div", "pl-slider-readout");
  const input = document.createElement("input");
  input.type = "range";
  input.className = "pl-slider";
  input.min = String(mn); input.max = String(mx); input.step = String(step);
  input.value = String(mn + (mx - mn) / 2);
  let touched = false;
  const fmt = (v) => {
    const tplStr = spec.readout || `{v}${spec.unit ? " " + spec.unit : ""}`;
    const rounded = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
    return tplStr.replace("{v}", rounded);
  };
  const cons = spec.consequence && typeof spec.consequence === "object" &&
    typeof spec.consequence.template === "string" ? spec.consequence : null;
  let consEl = null;
  const fmtCons = (v) => {
    const dec = Number.isInteger(cons.decimals)
      ? Math.max(0, Math.min(4, cons.decimals)) : 2;
    const p = Math.pow(10, dec);
    const y = Math.round(consequenceValue(cons.map, v) * p) / p;
    const raw = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
    return cons.template.replace("{y}", y).replace("{v}", raw);
  };
  const update = () => {
    const v = Number(input.value);
    readout.textContent = fmt(v);
    if (consEl) consEl.textContent = fmtCons(v);
  };
  input.addEventListener("input", () => {
    touched = true;
    update();
    cb.onInteract && cb.onInteract();
    cb.onComplete && cb.onComplete();
  });
  wrap.appendChild(readout);
  wrap.appendChild(input);
  const ends = el("div", "pl-slider-ends");
  ends.appendChild(el("span", null, fmt(mn)));
  ends.appendChild(el("span", null, fmt(mx)));
  wrap.appendChild(ends);
  if (cons) {
    consEl = el("div", "pl-consequence");
    wrap.appendChild(consEl);
  }
  update();
  root.appendChild(wrap);
  return {
    template: "slider_sim",
    isComplete: () => touched,
    check: () => {
      const ok = Math.abs(Number(input.value) - target) <= effTol;
      wrap.classList.toggle("right", ok);
      wrap.classList.toggle("wrong", !ok);
      return { correct: ok };
    },
    reset: () => {
      touched = false;
      input.value = String(mn + (mx - mn) / 2);
      update();
      wrap.classList.remove("right", "wrong");
    },
    highlightTarget: () => pulse(input),
    destroy: () => { root.innerHTML = ""; },
  };
}

/* ============================ number_line ============================ */
function renderNumberLine(spec, root, cb) {
  const mn = Number(spec.min), mx = Number(spec.max);
  const target = Number(spec.target);
  const tol = Number(spec.tolerance);
  const ticks = Math.max(2, Math.min(21, Number(spec.ticks) || 11));
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const wrap = el("div", "pl-numline-wrap");
  const track = el("div", "pl-numline-track");
  for (let i = 0; i < ticks; i++) {
    const t = el("span", "pl-numline-tick");
    const val = mn + ((mx - mn) * i) / (ticks - 1);
    t.style.left = `${(i / (ticks - 1)) * 100}%`;
    const lbl = el("span", "pl-numline-lbl",
      `${Math.round(val * 100) / 100}${spec.unit ? " " + spec.unit : ""}`);
    t.appendChild(lbl);
    track.appendChild(t);
  }
  const input = document.createElement("input");
  input.type = "range";
  input.className = "pl-numline-input";
  input.min = String(mn); input.max = String(mx);
  input.step = String((mx - mn) / 1000);
  input.value = String(mn);
  const readout = el("div", "pl-numline-readout", "—");
  let touched = false;
  input.addEventListener("input", () => {
    touched = true;
    const v = Math.round(Number(input.value) * 100) / 100;
    readout.textContent = `${v}${spec.unit ? " " + spec.unit : ""}`;
    cb.onInteract && cb.onInteract();
    cb.onComplete && cb.onComplete();
  });
  wrap.appendChild(readout);
  wrap.appendChild(track);
  wrap.appendChild(input);
  root.appendChild(wrap);
  return {
    template: "number_line",
    isComplete: () => touched,
    check: () => {
      const ok = Math.abs(Number(input.value) - target) <= tol;
      wrap.classList.toggle("right", ok);
      wrap.classList.toggle("wrong", !ok);
      return { correct: ok };
    },
    reset: () => {
      touched = false; input.value = String(mn);
      readout.textContent = "—"; wrap.classList.remove("right", "wrong");
    },
    highlightTarget: () => pulse(input),
    destroy: () => { root.innerHTML = ""; },
  };
}

/* ============================ fill_the_blank ========================== */
function renderFillBlank(spec, root, cb) {
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const sentence = el("div", "pl-fill-sentence");
  const blanks = spec.blanks || [];
  const filled = new Array(blanks.length).fill(null);
  const slots = [];
  const parts = String(spec.text || "").split("___");
  parts.forEach((part, i) => {
    if (part) sentence.appendChild(document.createTextNode(part));
    if (i < blanks.length) {
      const slot = el("span", "pl-fill-slot", "____");
      slot.dataset.idx = String(i);
      slots.push(slot);
      sentence.appendChild(slot);
    }
  });
  root.appendChild(sentence);

  // one chip bank per blank, shown under the sentence
  const banks = el("div", "pl-fill-banks");
  blanks.forEach((b, i) => {
    const bank = el("div", "pl-fill-bank");
    bank.appendChild(el("span", "pl-fill-bank-tag", `Blank ${i + 1}`));
    shuffle(b.choices || []).forEach((choice) => {
      const chip = el("button", "pl-chip");
      chip.type = "button";
      chip.textContent = choice;
      chip.addEventListener("click", () => {
        filled[i] = choice;
        slots[i].textContent = choice;
        slots[i].classList.add("done");
        bank.querySelectorAll(".pl-chip").forEach((c) => c.classList.remove("sel"));
        chip.classList.add("sel");
        cb.onInteract && cb.onInteract();
        if (filled.every((f) => f != null) && cb.onComplete) cb.onComplete();
      });
      bank.appendChild(chip);
    });
    banks.appendChild(bank);
  });
  root.appendChild(banks);

  return {
    template: "fill_the_blank",
    isComplete: () => filled.every((f) => f != null),
    check: () => {
      let ok = true;
      blanks.forEach((b, i) => {
        const good = filled[i] != null &&
          String(filled[i]).trim().toLowerCase() ===
          String(b.answer).trim().toLowerCase();
        slots[i].classList.toggle("right", good);
        slots[i].classList.toggle("wrong", !good && filled[i] != null);
        if (!good) ok = false;
      });
      return { correct: ok };
    },
    reset: () => {
      filled.fill(null);
      slots.forEach((s) => { s.textContent = "____"; s.classList.remove("done", "right", "wrong"); });
      banks.querySelectorAll(".pl-chip").forEach((c) => c.classList.remove("sel"));
    },
    highlightTarget: () => pulse(slots.find((s) => !s.classList.contains("done")) || sentence),
    destroy: () => { root.innerHTML = ""; },
  };
}

/* ============================ sort_order ============================ */
function renderSortOrder(spec, root, cb) {
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const items = spec.items || [];
  const correctOrder = (spec.correctOrder || []).map(String);
  let order = shuffle(items.map((it) => String(it.id)));
  // guard against an accidentally-correct shuffle
  if (order.join() === correctOrder.join() && order.length > 1) {
    order = order.slice().reverse();
  }
  const labelById = {};
  items.forEach((it) => (labelById[String(it.id)] = it.label));
  const list = el("ol", "pl-sort-list");

  function rerender() {
    list.innerHTML = "";
    order.forEach((id, idx) => {
      const li = el("li", "pl-sort-item");
      li.appendChild(el("span", "pl-sort-label", labelById[id]));
      const ctrls = el("span", "pl-sort-ctrls");
      const up = el("button", "pl-sort-btn", "▲");
      up.type = "button"; up.disabled = idx === 0;
      up.addEventListener("click", () => move(idx, -1));
      const down = el("button", "pl-sort-btn", "▼");
      down.type = "button"; down.disabled = idx === order.length - 1;
      down.addEventListener("click", () => move(idx, 1));
      ctrls.appendChild(up); ctrls.appendChild(down);
      li.appendChild(ctrls);
      list.appendChild(li);
    });
  }
  function move(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    rerender();
    cb.onInteract && cb.onInteract();
    cb.onComplete && cb.onComplete();
  }
  rerender();
  root.appendChild(list);
  return {
    template: "sort_order",
    isComplete: () => true,
    check: () => {
      const ok = order.join() === correctOrder.join();
      list.classList.toggle("right", ok);
      list.classList.toggle("wrong", !ok);
      return { correct: ok };
    },
    reset: () => {
      order = shuffle(items.map((it) => String(it.id)));
      if (order.join() === correctOrder.join() && order.length > 1) order.reverse();
      list.classList.remove("right", "wrong");
      rerender();
    },
    highlightTarget: () => pulse(list),
    destroy: () => { root.innerHTML = ""; },
  };
}

/* ============================ drag_tiles ============================ */
function renderDragTiles(spec, root, cb) {
  root.appendChild(el("p", "pl-instruction", spec.instruction || ""));
  const tiles = spec.tiles || [];
  const buckets = spec.buckets || [];
  const answer = spec.answer || {};
  // placement[tileId] = bucketId | null
  const placement = {};
  tiles.forEach((t) => (placement[String(t.id)] = null));
  let selected = null; // tile id currently "picked up"

  const tray = el("div", "pl-drag-tray");
  const tileEls = {};
  const bucketEls = {};

  function makeTile(t) {
    const tile = el("button", "pl-tile");
    tile.type = "button";
    tile.textContent = t.label;
    tile.dataset.id = String(t.id);
    tile.draggable = true;
    tile.addEventListener("click", () => pick(String(t.id), tile));
    tile.addEventListener("dragstart", (e) => {
      selected = String(t.id);
      try { e.dataTransfer.setData("text/plain", selected); } catch (_) {}
    });
    tileEls[String(t.id)] = tile;
    return tile;
  }
  function pick(id, tileNode) {
    if (selected === id) { selected = null; tileNode.classList.remove("picked"); return; }
    selected = id;
    Object.values(tileEls).forEach((x) => x.classList.remove("picked"));
    tileNode.classList.add("picked");
    cb.onInteract && cb.onInteract();
  }
  function place(tileId, bucketId) {
    placement[tileId] = bucketId;
    selected = null;
    cb.onInteract && cb.onInteract();
    rerender();
    if (Object.values(placement).every((v) => v != null) && cb.onComplete) cb.onComplete();
  }
  function rerender() {
    tray.innerHTML = "";
    const trayLabel = el("span", "pl-drag-tray-tag", "Tiles");
    tray.appendChild(trayLabel);
    tiles.forEach((t) => {
      const id = String(t.id);
      if (placement[id] == null) tray.appendChild(makeTile(t));
    });
    buckets.forEach((b) => {
      const bid = String(b.id);
      const box = bucketEls[bid];
      const body = box.querySelector(".pl-bucket-body");
      body.innerHTML = "";
      tiles.forEach((t) => {
        const id = String(t.id);
        if (placement[id] === bid) {
          const chip = makeTile(t);
          chip.classList.add("placed");
          chip.addEventListener("click", (e) => {
            e.stopPropagation();
            placement[id] = null; rerender();
          });
          body.appendChild(chip);
        }
      });
      box.classList.remove("right", "wrong");
    });
  }

  tiles.forEach(() => {}); // (no-op, structure clarity)
  root.appendChild(tray);

  const bucketRow = el("div", "pl-buckets");
  buckets.forEach((b) => {
    const bid = String(b.id);
    const box = el("div", "pl-bucket");
    box.appendChild(el("div", "pl-bucket-head", b.label));
    box.appendChild(el("div", "pl-bucket-body"));
    box.addEventListener("click", () => { if (selected) place(selected, bid); });
    box.addEventListener("dragover", (e) => { e.preventDefault(); box.classList.add("over"); });
    box.addEventListener("dragleave", () => box.classList.remove("over"));
    box.addEventListener("drop", (e) => {
      e.preventDefault(); box.classList.remove("over");
      let id = selected;
      try { id = e.dataTransfer.getData("text/plain") || selected; } catch (_) {}
      if (id) place(id, bid);
    });
    bucketEls[bid] = box;
    bucketRow.appendChild(box);
  });
  root.appendChild(bucketRow);
  rerender();

  return {
    template: "drag_tiles",
    isComplete: () => Object.values(placement).every((v) => v != null),
    check: () => {
      let ok = true;
      tiles.forEach((t) => {
        const id = String(t.id);
        if (String(placement[id]) !== String(answer[id])) ok = false;
      });
      buckets.forEach((b) => {
        const bid = String(b.id);
        const box = bucketEls[bid];
        let bucketOk = true;
        tiles.forEach((t) => {
          const id = String(t.id);
          if (placement[id] === bid && String(answer[id]) !== bid) bucketOk = false;
        });
        box.classList.toggle("right", ok || bucketOk);
        box.classList.toggle("wrong", !bucketOk);
      });
      return { correct: ok };
    },
    reset: () => {
      tiles.forEach((t) => (placement[String(t.id)] = null));
      selected = null; rerender();
    },
    highlightTarget: () => pulse(tray),
    destroy: () => { root.innerHTML = ""; },
  };
}

const RENDERERS = {
  tap_the_right_one: renderTapOne,
  slider_sim: renderSlider,
  number_line: renderNumberLine,
  fill_the_blank: renderFillBlank,
  sort_order: renderSortOrder,
  drag_tiles: renderDragTiles,
};

/**
 * Render a problem `spec` into `container`. Returns the widget instance, or
 * null if the template is unknown. Clears the container first.
 */
export function renderProblem(problem, container, callbacks = {}) {
  if (!problem || !problem.spec || !container) return null;
  const fn = RENDERERS[problem.template];
  if (!fn) {
    container.innerHTML =
      '<p class="pl-instruction">This problem could not be displayed.</p>';
    return null;
  }
  container.innerHTML = "";
  const cb = {
    onInteract: callbacks.onInteract || (() => {}),
    onComplete: callbacks.onComplete || (() => {}),
  };
  try {
    return fn(problem.spec, container, cb);
  } catch (e) {
    console.error("[ProblemTemplates] render error", e);
    container.innerHTML =
      '<p class="pl-instruction">This problem could not be displayed.</p>';
    return null;
  }
}

export const PROBLEM_TEMPLATE_IDS = Object.keys(RENDERERS);
